import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@/ui';
import { useT } from '@/i18n';

/**
 * Öffentliche Seiten ohne Service Worker: Zuschauer (/live) und Teilnehmer
 * (/anmelden) öffnen die App einmal über einen Link — für sie lohnt es nicht,
 * die ganze App in den Cache zu laden, und „Neue Version verfügbar" hätte sie
 * nur verwirrt.
 */
const OHNE_SW = ['/live', '/anmelden'];

/** „Später" fragt nach dieser Zeit noch einmal — sonst blieb ein alter Stand wochenlang stehen */
const SPAETER_MS = 30 * 60 * 1000;

/** Neuer App-Stand wird erst nach Bestätigung aktiv — nie mitten im Turnier (docs/08 §2.1) */
export function UpdatePrompt() {
  const { pathname } = useLocation();
  if (OHNE_SW.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null;
  return <UpdatePromptAktiv />;
}

function UpdatePromptAktiv() {
  const t = useT();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW();
  const erneut = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(erneut.current), []);
  const spaeter = () => {
    setNeedRefresh(false);
    window.clearTimeout(erneut.current);
    // Der neue Stand wartet weiter im Hintergrund — in einer halben Stunde noch einmal fragen
    erneut.current = window.setTimeout(() => setNeedRefresh(true), SPAETER_MS);
  };

  if (!needRefresh && !offlineReady) return null;
  return (
    <div
      className="fixed top-3 md:top-auto md:bottom-4 left-3 md:left-4 right-3 md:right-auto z-50 bg-panel border border-line px-4 py-3 text-sm flex items-center gap-3 shadow-lg max-w-md flex-wrap"
      role="status"
    >
      {needRefresh ? (
        <>
          <span className="flex-1 min-w-[12rem]">{t('Neue Version verfügbar. Jetzt aktualisieren? Laufende Eingaben bleiben erhalten.')}</span>
          <Button size="sm" variant="primary" onClick={() => updateServiceWorker(true)}>
            {t('Aktualisieren')}
          </Button>
          <Button size="sm" variant="ghost" onClick={spaeter} title={t('In einer halben Stunde noch einmal fragen')}>
            {t('Später')}
          </Button>
        </>
      ) : (
        <>
          <span className="text-live flex-1">{t('Airmail ist jetzt offline verfügbar.')}</span>
          <Button size="sm" variant="ghost" onClick={() => setOfflineReady(false)} data-pwa-ok>
            OK
          </Button>
        </>
      )}
    </div>
  );
}
